import axios from 'axios';
import { env } from 'env';
import {
  IDynamicMapTrigger,
  IDynamicMapTriggersResponse,
} from '@shared/interfaces/dynamicMapTrigger';

/**
 * Client for AI triggers reported by dynamically-spawned level chunks —
 * triggers with no corresponding static region in the map's MapTemplate.
 */
export default class DynamicMapTriggersService {
  /**
   * Retrieves every dynamic AI trigger registered for a map.
   *
   * @param mapId - Map to list dynamic triggers for.
   * @returns The map's registered dynamic triggers.
   */
  static getDynamicMapTriggers(mapId: string): Promise<IDynamicMapTrigger[]> {
    const url = `${env.REACT_APP_API_URL}api/v2/maps/${mapId}/dynamic-triggers`;
    return axios
      .get<IDynamicMapTriggersResponse>(url)
      .then((res) => res.data.triggers);
  }

  /**
   * Registers a dynamic AI trigger the first time it's reported, or returns
   * the existing row when it was already registered.
   *
   * @param mapId - Map the trigger was reported on.
   * @param triggerName - UE-reported trigger identifier.
   * @returns The registered trigger.
   */
  static registerDynamicMapTrigger(
    mapId: string,
    triggerName: string,
  ): Promise<IDynamicMapTrigger> {
    const url = `${env.REACT_APP_API_URL}api/v2/maps/${mapId}/dynamic-triggers`;
    return axios
      .post<IDynamicMapTrigger>(url, { triggerName })
      .then((res) => res.data);
  }

  /**
   * Deletes a registered dynamic AI trigger. Does not cascade to scripts or
   * knowledge items bound to it — the caller deletes those separately first.
   *
   * @param mapId - Map the trigger belongs to.
   * @param triggerName - UE-reported trigger identifier.
   */
  static deleteDynamicMapTrigger(
    mapId: string,
    triggerName: string,
  ): Promise<void> {
    const url = `${
      env.REACT_APP_API_URL
    }api/v2/maps/${mapId}/dynamic-triggers/${encodeURIComponent(triggerName)}`;
    return axios.delete(url).then(() => undefined);
  }
}
